import { useEffect, useState } from "react";
import { Chip, CircularProgress } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import QuoteRepository, { QuoteInfo } from "../../../Repositories/QuoteRepository";
import ButtonField from "../../Ui/ButtonField";
import { BrandContent, useTailwindBrand } from "../../shared";

interface Props {
    courseId: number;
    /** Rendu resserré pour une intégration dans le détail d'un cours. */
    compact?: boolean;
}

/**
 * P2 – Affichage du devis actif d'un cours déclaré : numéro, montant,
 * statut et ouverture du PDF (URL pré-signée re-demandée au clic pour
 * rester valide).
 */
const QuoteViewer = ({ courseId, compact = false }: Props) => {
    const { classes, common, brandConfig } = useTailwindBrand();
    const [loading, setLoading] = useState(true);
    const [quote, setQuote] = useState<QuoteInfo | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        QuoteRepository.getQuoteForCourse(courseId)
            .then((response) => {
                if (!cancelled) setQuote(response?.quote ?? null);
            })
            .catch(() => {
                if (!cancelled) setQuote(null);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [courseId]);

    const handleOpen = () => {
        if (!quote) return;
        QuoteRepository.download(quote.id)
            .then((response) => {
                if (response?.url) window.open(response.url, "_blank", "noopener");
            })
            .catch(() => undefined);
    };

    if (loading) {
        return (
            <div className="flex items-center gap-2 p-3">
                <CircularProgress size={18} />
                <BrandContent tag="p" children={<>Récupération du devis…</>} type="description" className={common.body} />
            </div>
        );
    }

    if (!quote) {
        // le devis peut mettre quelques instants à être émis : rester discret
        return null;
    }

    return (
        <div className={`w-full rounded-lg border border-gray-300 ${compact ? "p-3" : "p-4"} bg-gray-50`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-start gap-3">
                    <DescriptionOutlinedIcon sx={{ color: brandConfig.colors.primary }} />
                    <div>
                        <BrandContent
                            tag="p"
                            children={<>
                                Devis <strong>{quote.quoteNumber}</strong>{" "}
                                <Chip
                                    label={quote.status === "active" ? "Actif" : "Remplacé"}
                                    size="small"
                                    color={quote.status === "active" ? "success" : "default"}
                                    variant="outlined"
                                    sx={{ height: 18, fontSize: "0.65rem", ml: 0.5 }}
                                />
                            </>}
                            type="description"
                            className={`font-bold ${classes.primaryText}`}
                        />
                        <BrandContent
                            tag="p"
                            children={<>
                                Montant TTC : <strong>{quote.totalAmount.toFixed(2)} €</strong>
                                {quote.remainingAmount !== null && (
                                    <>  reste à charge estimé après Avance Immédiate : <strong>{quote.remainingAmount.toFixed(2)} €</strong></>
                                )}
                            </>}
                            type="description"
                            className={common.caption}
                        />
                        {Array.isArray(quote.replaces) && quote.replaces.length > 0 && (
                            <BrandContent
                                tag="p"
                                children={<>
                                    Remplace {quote.replaces.length > 1 ? "les devis" : "le devis"} n° {quote.replaces.join(", ")}
                                </>}
                                type="description"
                                className={common.caption}
                            />
                        )}
                    </div>
                </div>
                <ButtonField
                    label="Voir le devis"
                    variant="outlined"
                    startIcon={<OpenInNewRoundedIcon fontSize="small" />}
                    onClick={handleOpen}
                />
            </div>
        </div>
    );
};

export default QuoteViewer;
